<template>
  <div class="student-list">
    <Preloader v-if="isLoading" />
    <img class="student-list__logo" src="@/assets/images/brand/logo_full.svg" alt="Early Stage" />

    <Survey v-if="isSurveyEnabled" />

    <div v-if="getAgentData || getAdditionalAccessData" class="student-list__container">
      <AgentItem v-if="getAgentData" :agent="getAgentData" @click.native="selectAgent(getAgentData)" />
      <AgentItem v-if="getAdditionalAccessData" :agent="getAdditionalAccessData" :hasAdditionalAccess="true"
        @click.native="selectAdditionalAccess(getAdditionalAccessData)" />
    </div>
    <!-- <template v-if="profile && profile.parent"> -->
    <div v-if="hasStudents" class="student-list__container">
      <WelcomeModal v-if="!isLoading" v-model="welcomeModalOpen" />
      <StudentItem v-for="student in students" :key="student.id" :student="student"
        @click.native="selectStudent(student)" />
    </div>
    <div v-else class="student-list__empty">
      <template v-if="isOnlyParentRole && !hasAdditionalAccessRole">
        <div class="student-list__title">{{ $t('dashboard.empty_students_title') }}</div>
        <div class="student-list__desc">{{ $t('dashboard.empty_students_desc') }}</div>
      </template>
      <div class="student-list__kids">
        <div class="kid_first-wrapper">
          <img class="kid_first-mobile" src="@/assets/images/welcome/kid_first-mobile.svg" />
          <img class="kid_first-desktop" src="@/assets/images/welcome/kid_first-desktop.svg" />
        </div>
        <div class="kid_second-wrapper">
          <img class="kid_second-mobile" src="@/assets/images/welcome/kid_second-mobile.svg" />
          <img class="kid_second-desktop" src="@/assets/images/welcome/kid_second-desktop.svg" />
        </div>
      </div>
    </div>
    <!-- </template> -->

    <div class="student-list__utillities">
      <div class="student-list__utillities__item">
        <router-link :to="{ name: 'Auth.ChangePassword' }">
          <img src="@/assets/images/icons/settings.svg" />
          <span>{{ $t('auth.settings') }}</span>
        </router-link>
      </div>
      <div class="student-list__utillities__item">
        <button @click="logoutUser" :class="{ 'inactive': logoutInProgress }">
          <img src="@/assets/images/icons/logout.svg" />
          <span>{{ $t('auth.logout') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  mapState, mapActions, mapMutations, mapGetters,
} from 'vuex';
import * as Sentry from '@sentry/vue';

// eslint-disable-next-line import/no-cycle
import VueStore from '@/store';
import StudentItem from '@/components/StudentItem.vue';
import AgentItem from '@/components/AgentItem.vue';
import Survey from '@/components/Survey.vue';
import Preloader from '@/components/Preloader.vue';
import WelcomeModal from '@/components/Popup/WelcomeModal.vue';

class EmptyListError extends Error {
  constructor(message) {
    super(message);
    this.name = 'Logowanie użytkownika z pustą listą dzieci';
  }
}

export default {
  name: 'Welcome',
  components: {
    Survey,
    StudentItem,
    Preloader,
    WelcomeModal,
    AgentItem,
  },

  data() {
    return {
      isSurveyEnabled: false,
      logoutInProgress: false,
      welcomeModalOpen: false,
    };
  },

  computed: {
    ...mapState('students', ['students']),
    ...mapState('layout', ['isLoading']),
    ...mapState('auth', ['user']),
    ...mapGetters('auth', [
      'isFullAccess',
      'isOnlyAgentRole',
      'isOnlyParentRole',
      'hasAdditionalAccessRole',
      'getAgentData',
      'getAdditionalAccessData',
    ]),
    ...mapGetters('onboarding', [
      'getOnboardingProperty',
    ]),
    hasStudents() {
      return !!this.students && !!this.students.length;
    },
    isMobileOnboardingDone() {
      return this.getOnboardingProperty({ deviceType: 'mobile', property: 'welcomeModal' });
    },
    isDesktopOnboardingDone() {
      return this.getOnboardingProperty({ deviceType: 'desktop', property: 'welcomeModal' });
    },
  },
  beforeRouteEnter(to, from, next) {
    if (VueStore.getters['auth/isOfficeLogin']) {
      VueStore.commit('auth/clearAuth');
      VueStore.dispatch('auth/clearStore');
      next({ name: 'Auth.Login' });
    } else next();
  },
  async created() {
    if (this.isOnlyAgentRole || this.hasAdditionalAccessRole) return;

    this.$store.commit('layout/startLoading');
    try {
      await this.getProfile();

      if (!this.isDesktopOnboardingDone) {
        this.welcomeModalOpen = true;
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.$store.commit('layout/finishLoading');
    }
  },
  methods: {
    ...mapMutations('materials', ['setRealizations']),
    ...mapActions('students', ['getProfile']),
    ...mapMutations('auth', ['setCurrentProfile', 'setAccess']),
    ...mapActions('auth', ['clearStore']),

    emptyListException(parentID) {
      Sentry.withScope((scope) => {
        scope.setTag('parent ID', parentID);
        scope.setLevel('info');
        Sentry.captureException(new EmptyListError(`ID rodzica: ${parentID}`));
      });
    },

    async logoutUser() {
      this.logoutInProgress = true;
      try {
        await this.$store.dispatch('auth/logout');
      } catch (e) {
        console.error(e);
      } finally {
        this.logoutInProgress = false;
      }
    },
    selectAgent(agent) {
      this.setCurrentProfile('ROLE_LECTOR');
      this.setRealizations(agent.applications);
      this.setAccess('demo');
      this.routerPush({
        name: 'Main.OnlineMaterials',
      });
    },
    selectAdditionalAccess(profile) {
      this.setCurrentProfile('ROLE_WEB_MUSIC_ADDITIONAL_ACCESS');
      this.setRealizations(profile.applications);
      this.setAccess('demo');
      this.routerPush({
        name: 'Main.OnlineMaterials',
      });
    },
    selectStudent(student) {
      this.setCurrentProfile('ROLE_PARENT');
      this.setRealizations(student.applications);
      this.setAccess(student.access);
      this.$store.commit('students/setStudent', student);
      if (this.isFullAccess) {
        if (student?.group?.hasHomeworks) {
          this.routerPush({
            name: 'Main.Homeworks',
            params: { kidId: student.id },
          });
        } else {
          this.routerPush({
            name: 'Main.Attendances',
            params: { kidId: student.id },
          });
        }
      } else {
        this.routerPush({
          name: 'Main.OnlineMaterials',
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.inactive {
  @apply opacity-25 pointer-events-none;
}

.student-list {
  @apply relative flex flex-col justify-between items-center mx-auto my-0 text-center;
  min-height: 100vh;
  overflow: hidden;

  @media screen and (min-width: 768px) {
    @apply justify-start;
    max-width: 800px;
  }

  @media screen and (min-width: 992px) {
    max-width: 1200px;
  }

  &__logo {
    margin-bottom: 43px;
    @apply h-auto mt-12 block mx-auto;
    width: 200px;

    @media screen and (min-width: 768px) {
      margin-bottom: 53px;
      @apply mt-24;
      width: 410px;
    }
  }

  &__container {
    max-width: 400px;
    @apply w-full flex flex-col mb-6;
    margin-top: 33px;

    @media screen and (min-width: 768px) {
      max-width: none;
      @apply flex-row w-full justify-center flex-wrap;
      margin-top: 66px;
    }
  }

  &__utillities {
    @apply flex flex-col my-3;

    @media screen and (min-width: 768px) {
      @apply fixed flex-row-reverse mx-12 mt-6;
      right: 0;
      top: 0;
    }

    &__item>a,
    &__item>button {
      @apply flex justify-center items-center mx-3 my-2 no-underline;
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

      &:hover {
        @apply text-grey-darkest;
      }

      span {
        @apply mx-2;
      }
    }
  }

  &__empty {
    @apply relative px-3;
    max-width: 1000px;
    line-height: 1.2;

    @media screen and (min-width: 768px) {
      @apply pt-16;
    }

    @media screen and (min-width: 1600px) {
      @apply pt-24;
    }
  }

  &__title {
    font-weight: 700;
    margin-top: 2rem;
    margin-bottom: 1.2rem;

    @media screen and (min-width: 768px) {
      @apply text-2xl;
    }
  }

  &__desc {
    @apply leading-tight;
    margin-top: 1rem;
    margin-bottom: 1.5rem;

    @media screen and (min-width: 768px) {
      @apply text-xl;
      margin-top: 1.5rem;
      margin-bottom: 3.5rem;
    }
  }

  &__kids {
    @apply flex justify-between items-center w-full pt-4 pb-2;
    min-width: 300px;

    @media screen and (min-width: 768px) {
      @apply pt-12 pb-6;
      min-width: 748px;
    }
  }

  .kid_first-wrapper,
  .kid_second-wrapper {
    .kid_first-mobile {
      display: block;
      width: 100px;

      @media screen and (min-width: 768px) {
        display: none;
      }
    }

    .kid_second-mobile {
      display: block;
      width: 160px;

      @media screen and (min-width: 768px) {
        display: none;
      }
    }

    .kid_first-desktop {
      display: none;

      @media screen and (min-width: 768px) {
        display: block;
        width: 180px;
      }
    }

    .kid_second-desktop {
      display: none;

      @media screen and (min-width: 768px) {
        display: block;
        width: 250px;
      }
    }
  }
}
</style>
